<template>
    <v-dialog v-model="dialogSwitch" max-width="550" scrollable>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span class="text-h6">
                    <v-icon size="20" class="me-1">mdi-note-text</v-icon>
                    Notes
                    <v-chip v-if="notes.length" size="x-small" class="ms-1">{{ notes.length }}</v-chip>
                </span>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue', false)"/>
            </v-card-title>

            <v-card-text class="pa-4" style="min-height: 200px; max-height: 400px;">
                <!-- Add note form -->
                <div class="d-flex ga-2 mb-4">
                    <v-textarea v-model="newNote" rows="2" auto-grow density="compact" variant="outlined"
                                placeholder="Write a note..." hide-details class="flex-grow-1"
                                @keydown.ctrl.enter="addNote"/>
                    <v-btn color="primary" icon="mdi-send" size="small" :loading="saving" :disabled="!newNote?.trim()"
                           @click="addNote" class="mt-1"/>
                </div>

                <!-- Notes list -->
                <div v-if="loading" class="text-center py-4">
                    <v-progress-circular indeterminate color="primary" size="24"/>
                </div>

                <div v-else-if="!notes.length" class="text-center py-6 text-grey">
                    <v-icon size="40" color="grey-lighten-1">mdi-note-off-outline</v-icon>
                    <div class="mt-2">No notes yet</div>
                </div>

                <template v-else>
                    <v-card v-for="note in notes" :key="note.id" variant="outlined" class="mb-2"
                            :color="note.is_pinned ? 'amber' : 'grey'"
                            :style="{'background-color': note.is_pinned ? '#FFFDE7' : '#FAFAFA'}"
                    >
                        <v-card-text class="pa-3">
                            <!-- Pin badge -->
                            <v-chip v-if="note.is_pinned" size="x-small" color="amber" variant="flat"
                                    prepend-icon="mdi-pin" class="mb-1">Pinned</v-chip>

                            <!-- Note text (edit mode or display) -->
                            <div v-if="editingId === note.id">
                                <v-textarea v-model="editText" rows="2" auto-grow density="compact"
                                            variant="outlined" hide-details class="mb-2"/>
                                <div class="d-flex ga-2 justify-end">
                                    <v-btn size="x-small" variant="text" @click="editingId = null">Cancel</v-btn>
                                    <v-btn size="x-small" color="primary" :loading="saving" @click="saveEdit(note)">Save</v-btn>
                                </div>
                            </div>
                            <div v-else class="text-body-2" style="white-space: pre-wrap;">{{ note.note }}</div>

                            <!-- Author + actions -->
                            <div class="d-flex justify-space-between align-center mt-2">
                                <div class="text-caption text-grey">
                                    <UserAvatar :src="note.user?.avatar" :first-name="note.user?.first_name"
                                                :last-name="note.user?.last_name" size="16" class="me-1"/>
                                    {{ note.user?.first_name }} {{ note.user?.last_name }}
                                    · {{ timeAgo(note.updated_at) }}
                                </div>
                                <div v-if="editingId !== note.id" class="d-flex ga-1">
                                    <v-btn icon size="x-small" variant="text"
                                           :color="note.is_pinned ? 'amber' : 'grey'"
                                           @click="togglePin(note)">
                                        <v-icon size="14">{{ note.is_pinned ? 'mdi-pin' : 'mdi-pin-outline' }}</v-icon>
                                        <v-tooltip activator="parent" location="top">{{ note.is_pinned ? 'Unpin' : 'Pin' }}</v-tooltip>
                                    </v-btn>
                                    <v-btn icon size="x-small" variant="text" color="grey"
                                           @click="startEdit(note)">
                                        <v-icon size="14">mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn icon size="x-small" variant="text" color="red"
                                           @click="deleteNote(note)">
                                        <v-icon size="14">mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </template>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, watch, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import UserAvatar from "@/components/UserAvatar.vue";

const {showSnackbar} = useSnackbarStore();

const props = defineProps({
    modelValue: Boolean,
    entityType: {type: String, required: true},  // 'assets', 'users', 'companies'
    entityId: {type: [Number, String], required: true},
});

const emit = defineEmits(['update:modelValue', 'notesChanged']);
const dialogSwitch = ref(props.modelValue);

watch(() => props.modelValue, (v) => dialogSwitch.value = v);
watch(dialogSwitch, (v) => emit('update:modelValue', v));
watch(() => props.entityId, () => { notes.value = []; fetchNotes(); });

const notes = ref([]);
const loading = ref(false);
const saving = ref(false);
const newNote = ref('');
const editingId = ref(null);
const editText = ref('');

const baseUrl = () => `${props.entityType}/${props.entityId}/notes`;

onMounted(() => fetchNotes());

const fetchNotes = async () => {
    loading.value = true;
    try {
        const response = await send('get', baseUrl());
        notes.value = response.data?.data?.notes ?? [];
    } catch (e) {
        showSnackbar(e.response?.data?.message || 'Failed to load notes', 'error');
    } finally {
        loading.value = false;
    }
};

const addNote = async () => {
    if (!newNote.value?.trim()) return;
    saving.value = true;
    try {
        const response = await send('post', baseUrl(), {note: newNote.value.trim()});
        if (response.data?.success) {
            notes.value.unshift(response.data.data.note);
            newNote.value = '';
            emit('notesChanged', notes.value.length);
        } else {
            showSnackbar(response.data?.message || 'Failed to add note', 'error');
        }
    } catch (e) {
        showSnackbar(e.response?.data?.message || 'Failed to add note', 'error');
    } finally {
        saving.value = false;
    }
};

const startEdit = (note) => {
    editingId.value = note.id;
    editText.value = note.note;
};

const saveEdit = async (note) => {
    saving.value = true;
    try {
        const response = await send('put', `${baseUrl()}/${note.id}`, {note: editText.value.trim()});
        if (response.data?.success) {
            note.note = editText.value.trim();
            note.updated_at = new Date().toISOString();
            editingId.value = null;
        }
    } catch (e) {
        showSnackbar('Failed to update note', 'error');
    } finally {
        saving.value = false;
    }
};

const deleteNote = async (note) => {
    if (!confirm('Delete this note?')) return;
    try {
        await send('delete', `${baseUrl()}/${note.id}`);
        notes.value = notes.value.filter(n => n.id !== note.id);
        emit('notesChanged', notes.value.length);
    } catch (e) {
        showSnackbar('Failed to delete note', 'error');
    }
};

const togglePin = async (note) => {
    try {
        const response = await send('post', `${baseUrl()}/${note.id}/pin`);
        if (response.data?.success) {
            const updated = response.data.data.note;
            // Unpin all others if this was pinned
            if (updated.is_pinned) {
                notes.value.forEach(n => { if (n.id !== note.id) n.is_pinned = false; });
            }
            note.is_pinned = updated.is_pinned;
        }
    } catch (e) {
        showSnackbar('Failed to toggle pin', 'error');
    }
};

const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    // Server timestamps are UTC without 'Z' suffix
    const utcStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    const diff = (Date.now() - new Date(utcStr).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};
</script>
